import { c as _c } from "react/compiler-runtime";
import { FolderClosed, FolderKanban, FolderOpenIcon, HelpCircle, Plug, Shield } from "lucide-react";
import { useEffect, useMemo } from "react";
import { NavMain } from "@/components/nav-main";
import { NavNewConversation } from "@/components/nav-new-conversation";
import { NavRecentChats } from "@/components/nav-recent-chats";
import { NavUser } from "@/components/nav-user";
import { NavWorkspaceSwitcher } from "@/components/nav-workspace-switcher";
import { SidebarRailSection } from "@/components/sidebar/SidebarRailSection";
import { useAuth } from "@/contexts/auth-context";
import { useIsWorkbenchRoute, useNavigationData } from "@/hooks/useNavigationData";
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@/components/ui/sidebar";
import { PanelLeftClose, PanelRightClose } from "lucide-react";
import { useProjectStore } from "@/stores/useProjectStore";
import { cn } from "@/lib/utils";
const navOverviewItem = {
  title: "Projects",
  url: "/dashboard",
  // FolderKanban = project portfolio; workspace switcher keeps LayoutGrid.
  icon: FolderKanban
};
const navWayfindBase = [{
  title: "Plugins",
  url: "/dashboard/plugins",
  icon: Plug
}, {
  title: "Docs",
  url: "https://playbook.swiftcns.ai/",
  icon: HelpCircle,
  isExternal: true as const
}];
const navWayfindAdminItem = {
  title: "Admin",
  url: "/admin",
  icon: Shield
};
function SidebarTriggerIcon() {
  const $ = _c(2);
  const {
    state
  } = useSidebar();
  const Icon = state === "collapsed" ? PanelRightClose : PanelLeftClose;
  let t0;
  if ($[0] !== Icon) {
    t0 = <Icon className="size-4" />;
    $[0] = Icon;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
interface AppSidebarProps extends React.ComponentProps<typeof Sidebar> {}
export function AppSidebar(t0) {
  const $ = _c(50);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  const {
    fetchProjects
  } = useProjectStore();
  const {
    user,
    loading: authLoading
  } = useAuth();
  const {
    toggleSidebar,
    state
  } = useSidebar();
  const isCollapsed = state === "collapsed";
  const isWorkbenchRoute = useIsWorkbenchRoute();
  const {
    projectId,
    currentProject
  } = useNavigationData();
  let t1;
  let t2;
  if ($[2] !== fetchProjects || $[3] !== isWorkbenchRoute) {
    t1 = () => {
      if (isWorkbenchRoute && useProjectStore.getState().projects.length > 0) {
        return;
      }
      fetchProjects();
    };
    t2 = [fetchProjects, isWorkbenchRoute];
    $[2] = fetchProjects;
    $[3] = isWorkbenchRoute;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  let t3;
  if ($[6] !== user) {
    t3 = user ? {
      name: `${user.firstName} ${user.lastName}`,
      email: user.email,
      avatar: "/swiftracks.png"
    } : null;
    $[6] = user;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const displayUser = t3;
  user?.admin;
  currentProject?.title;
  let t4;
  bb0: {
    if (isWorkbenchRoute) {
      let items;
      if ($[8] !== currentProject?.title || $[9] !== projectId) {
        items = [navOverviewItem];
        if (projectId) {
          const t5 = currentProject?.title ?? "Project";
          const t6 = `/dashboard/projects/${projectId}`;
          let t7;
          if ($[11] !== t5 || $[12] !== t6) {
            t7 = {
              title: t5,
              url: t6,
              icon: FolderClosed,
              activeIcon: FolderOpenIcon
            };
            $[11] = t5;
            $[12] = t6;
            $[13] = t7;
          } else {
            t7 = $[13];
          }
          items.push(t7);
        }
        items.push(navWayfindBase[1]);
        $[8] = currentProject?.title;
        $[9] = projectId;
        $[10] = items;
      } else {
        items = $[10];
      }
      t4 = items;
      break bb0;
    }
    let t5;
    if ($[14] !== user?.admin) {
      t5 = [navOverviewItem, ...(user?.admin === true ? [navWayfindAdminItem] : []), navWayfindBase[0]];
      $[14] = user?.admin;
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    t4 = t5;
  }
  const navWayfind = t4;
  const workspaceSwitcherMode = isWorkbenchRoute && isCollapsed ? "compact" : "list";
  const showResumeSection = !isCollapsed || isWorkbenchRoute && isCollapsed;
  const compactWorkbenchHeader = isWorkbenchRoute && isCollapsed;
  const t5 = !compactWorkbenchHeader && "flex h-12 shrink-0 items-center border-b border-sidebar-border px-2";
  let t6;
  if ($[16] !== t5) {
    t6 = cn(t5);
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== toggleSidebar) {
    t7 = e => {
      e.preventDefault();
      toggleSidebar();
    };
    $[18] = toggleSidebar;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <SidebarTriggerIcon />;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t7) {
    t9 = <SidebarMenu><SidebarMenuItem><SidebarMenuButton size="lg" tooltip="Toggle Sidebar" onClick={t7} className="justify-center">{t8}</SidebarMenuButton></SidebarMenuItem></SidebarMenu>;
    $[21] = t7;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== t6 || $[24] !== t9) {
    t10 = <SidebarHeader className={t6}>{t9}</SidebarHeader>;
    $[23] = t6;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <SidebarRailSection><NavNewConversation /></SidebarRailSection>;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== isCollapsed || $[28] !== isWorkbenchRoute || $[29] !== showResumeSection) {
    t12 = showResumeSection ? <SidebarRailSection><NavRecentChats collapsedIconMode={isWorkbenchRoute && isCollapsed} /></SidebarRailSection> : null;
    $[27] = isCollapsed;
    $[28] = isWorkbenchRoute;
    $[29] = showResumeSection;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== navWayfind) {
    t13 = <SidebarRailSection><NavMain items={navWayfind} groupLabel="Navigate" /></SidebarRailSection>;
    $[31] = navWayfind;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  const t14 = !isWorkbenchRoute;
  let t15;
  if ($[33] !== t14 || $[34] !== workspaceSwitcherMode) {
    t15 = <SidebarRailSection className="border-b-0"><NavWorkspaceSwitcher mode={workspaceSwitcherMode} showWorkspaceInvite={t14} /></SidebarRailSection>;
    $[33] = t14;
    $[34] = workspaceSwitcherMode;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== t12 || $[37] !== t13 || $[38] !== t15) {
    t16 = <SidebarContent>{t11}{t12}{t13}{t15}</SidebarContent>;
    $[36] = t12;
    $[37] = t13;
    $[38] = t15;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== authLoading || $[41] !== displayUser) {
    t17 = authLoading ? <div className="p-4 text-center text-sm text-muted-foreground">Loading user...</div> : displayUser ? <NavUser user={displayUser} /> : null;
    $[40] = authLoading;
    $[41] = displayUser;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] !== t17) {
    t18 = <SidebarFooter>{t17}</SidebarFooter>;
    $[43] = t17;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] !== props || $[46] !== t10 || $[47] !== t16 || $[48] !== t18) {
    t19 = <Sidebar variant="inset" collapsible="icon" {...props}>{t10}{t16}{t18}</Sidebar>;
    $[45] = props;
    $[46] = t10;
    $[47] = t16;
    $[48] = t18;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  return t19;
}